import { useState, MouseEvent, FC, useContext, useRef } from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Avatar,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import __ from "src/utils/Translations";
import { Edit } from "@benefit-systems/icons";
import defaultStructureImage from "src/Pages/Company/OrganizationStructure/assets/icons/DefaultOrgchartManagerIcon.svg";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import ImageUpload from "src/Components/ImageUpload/ImageUpload";
import { ImageCroppRef } from "src/Components/ImageCropp/ImageCropp";
import { styles } from "./editAvatarStyle";

interface EditAvatarProps {
  value: string | null;
  onChange: (avatar: string | null) => void;
}

export const EditAvatar: FC<EditAvatarProps> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { setModal, resetModal } = useContext(GlobalModalContext);
  const cropperRef = useRef<ImageCroppRef>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const handleRemoveImage = () => {
    onChange(null);
    handleCloseMenu();
  };

  const modalActions: {
    label: string;
    variant?: "outlined" | "text" | "contained";
    callbackFn: VoidFunction;
  }[] = [
    {
      label: __("Anuluj") as string,
      variant: "outlined",
      callbackFn: resetModal,
    },
    {
      label: __("Zapisz") as string,
      variant: "contained",
      callbackFn: async () => {
        await cropperRef.current?.submitCrop();
      },
    },
  ];

  return (
    <>
      <Box sx={styles.avatarContainer}>
        <Avatar
          src={value || defaultStructureImage}
          alt="avatar"
          sx={value ? styles.avatarStyle : styles.avatarEmpty}
        />
        <Box
          sx={styles.editButton}
          onClick={handleOpenMenu}
          data-testid="os__settings-avatar-upload"
        >
          <Edit width={13} fill="#FFF" />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseMenu}
        slotProps={{ paper: { sx: styles.menuPaper } }}
      >
        <MenuItem
          onClick={() => {
            setModal({
              open: true,
              title: (
                <Typography
                  sx={{
                    fontSize: "16px",
                    lineHeight: "32px",
                    fontWeight: 700,
                    mb: 0,
                  }}
                >
                  {__("Avatar struktury")}:
                </Typography>
              ),
              customElements: (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <ImageUpload
                    ref={cropperRef}
                    onImageCropped={(img) => {
                      const result =
                        typeof img === "string"
                          ? img
                          : URL.createObjectURL(img);
                      onChange(result);
                      resetModal();
                    }}
                  />
                </Box>
              ),
              actions: modalActions,
              maxWidth: 350,
            });
            handleCloseMenu();
          }}
          data-testid="os__settings-avatar-change-button"
        >
          <ListItemIcon>
            <SaveAltIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{__("Zmień zdjęcie")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleRemoveImage} data-testid="os__settings-avatar-delete-button">
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{__("Usuń zdjęcie")}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
